if (!customElements.get('product-upsell')) {
  customElements.define(
    'product-upsell',
    class ProductUpsell extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.addEventListener('change', this.onChange.bind(this), { signal });
        this.querySelector('[data-upsell-toggle]')?.addEventListener('click', this.onToggle.bind(this), { signal });

        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('submit', this.onSubmit.bind(this), { capture: true, signal });
        this.productInfo?.addEventListener('click', this.onBuyNowClick.bind(this), { capture: true, signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      onChange(event) {
        const select = event.target.closest('[data-upsell-variant-select]');
        if (!select) return;

        const item = select.closest('.product-upsell__item');
        const option = select.options[select.selectedIndex];
        const checkbox = item.querySelector('[data-upsell-variant]');
        const price = item.querySelector('[data-upsell-price]');
        const comparePrice = item.querySelector('[data-upsell-compare-price]');

        checkbox.value = select.value;
        price.textContent = option.dataset.price;
        comparePrice.textContent = option.dataset.comparePrice;
        comparePrice.hidden = !option.dataset.comparePrice;
      }

      onToggle(event) {
        const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';

        this.querySelectorAll('.product-upsell__item--extra').forEach((item) => {
          item.classList.toggle('hidden', expanded);
        });
        event.currentTarget.setAttribute('aria-expanded', String(!expanded));
        event.currentTarget.querySelector('[data-upsell-show-more]').classList.toggle('hidden', !expanded);
        event.currentTarget.querySelector('[data-upsell-show-less]').classList.toggle('hidden', expanded);
      }

      async onSubmit(event) {
        const selectedUpsells = [...this.querySelectorAll('[data-upsell-variant]:checked')];
        const productForm = event.target.closest('product-form');

        if (
          !productForm ||
          event.target.dataset.type !== 'add-to-cart-form' ||
          selectedUpsells.length === 0 ||
          productForm.closest('product-info') !== this.productInfo
        )
          return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const form = event.target;
        const formData = new FormData(form);
        const submitButton = productForm.querySelector('[type="submit"]');
        const spinner = productForm.querySelector('.loading__spinner');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.setLoading(submitButton, spinner, true);
        this.showError(productForm);

        try {
          const items = this.buildItems(formData, selectedUpsells);
          const body = { items };

          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || window.cartStrings?.error);
          }

          if (!cart) {
            window.location = routes.cart_url;
            return;
          }

          cart.classList.remove('is-empty');
          this.renderCart(cart, result, Number(formData.get('id')));

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-upsell',
              productVariantId: formData.get('id'),
              cartData: result,
            });
          }
        } catch (error) {
          this.showError(productForm, error.message);
        } finally {
          this.setLoading(submitButton, spinner, false);
        }
      }

      async onBuyNowClick(event) {
        const paymentButton = event
          .composedPath()
          .find(
            (element) =>
              element instanceof Element &&
              element.matches('.shopify-payment-button, [data-shopify="payment-button"]')
          );
        const selectedUpsells = [...this.querySelectorAll('[data-upsell-variant]:checked')];
        const productForm = paymentButton?.closest('product-form');

        if (
          !paymentButton ||
          selectedUpsells.length === 0 ||
          !productForm ||
          productForm.closest('product-info') !== this.productInfo ||
          this.buyNowPending
        )
          return;

        const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const formData = new FormData(form);
        const button = event
          .composedPath()
          .find(
            (element) =>
              element instanceof HTMLElement &&
              element.matches('.shopify-payment-button__button, shopify-accelerated-checkout')
          );

        this.buyNowPending = true;
        this.setBuyNowLoading(paymentButton, button, true);
        this.showError(productForm);

        try {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items: this.buildItems(formData, selectedUpsells) }),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || window.cartStrings?.error);
          }

          const root = window.Shopify?.routes?.root || '/';
          window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
        } catch (error) {
          this.showError(productForm, error.message);
          this.buyNowPending = false;
          this.setBuyNowLoading(paymentButton, button, false);
        }
      }

      // `/cart/add.js` answers a bare line item when posted a single variant, but the
      // multi-item payload used here answers `{ items: [...] }` instead. cart-notification
      // keys its markup by line item `key` and cart-drawer by `id`, so both are backfilled
      // before the response is handed to the theme's cart element.
      renderCart(cart, result, mainVariantId) {
        const addedItems = Array.isArray(result.items) ? result.items : [result];
        const primaryItem = addedItems.find((item) => Number(item.id) === mainVariantId) || addedItems[0];
        const section = result.sections?.['cart-notification-product']
          ? new DOMParser().parseFromString(result.sections['cart-notification-product'], 'text/html')
          : null;

        cart.renderContents({
          ...result,
          key: result.key || this.findRenderedKey(section, [primaryItem, ...addedItems]) || primaryItem?.key,
          id: result.id || primaryItem?.id,
        });

        this.renderNotificationItems(section, addedItems);
      }

      // cart-notification looks up its line item by key and throws when that key is
      // missing from the re-rendered section, so only offer a key that is present.
      findRenderedKey(section, items) {
        if (!section) return null;

        return items.find((item) => item?.key && this.findItemBlock(section, item.key))?.key || null;
      }

      // The notification markup holds a single line item, so render every line that was
      // just added to keep the chosen upsells visible without a cart refresh.
      renderNotificationItems(section, addedItems) {
        const container = document.getElementById('cart-notification-product');
        if (!section || !container || addedItems.length < 2) return;

        const markup = addedItems
          .map((item) => this.findItemBlock(section, item.key)?.outerHTML)
          .filter(Boolean)
          .join('');

        if (markup) container.innerHTML = markup;
      }

      findItemBlock(section, key) {
        return section.querySelector(`[id="cart-notification-product-${key}"]`);
      }

      buildItems(formData, selectedUpsells) {
        const mainVariantId = Number(formData.get('id'));
        const mainQuantity = Number(formData.get('quantity')) || 1;
        const properties = {};

        formData.forEach((value, key) => {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
        });

        const mainItem = {
          id: mainVariantId,
          quantity: mainQuantity,
        };

        if (Object.keys(properties).length) mainItem.properties = properties;
        if (formData.get('selling_plan')) mainItem.selling_plan = Number(formData.get('selling_plan'));

        const items = [mainItem];

        selectedUpsells.forEach((input) => {
          const variantId = Number(input.value);
          const existingItem = items.find((item) => item.id === variantId);

          if (existingItem) {
            existingItem.quantity += 1;
          } else {
            items.push({ id: variantId, quantity: 1 });
          }
        });

        return items;
      }

      setLoading(button, spinner, loading) {
        if (!button) return;

        button.toggleAttribute('aria-disabled', loading);
        button.classList.toggle('loading', loading);
        spinner?.classList.toggle('hidden', !loading);
      }

      setBuyNowLoading(paymentButton, button, loading) {
        paymentButton.toggleAttribute('aria-busy', loading);

        if (!button) return;

        button.toggleAttribute('aria-disabled', loading);
        if ('disabled' in button) button.disabled = loading;
      }

      showError(productForm, message = '') {
        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const messageElement = wrapper?.querySelector('.product-form__error-message');

        if (!wrapper || !messageElement) return;

        wrapper.toggleAttribute('hidden', !message);
        messageElement.textContent = message;
      }
    }
  );
}
